'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import AppNav from '@/components/AppNav';

interface TrademarkApplication {
  id: string;
  mark: string;
  class: string;
  appNumber: string;
  filedDate: string;
  status: 'examination' | 'advertised' | 'objected' | 'registered' | 'pending';
  renewalDue?: string;
  daysToRenewal?: number;
}

interface GovernmentUpdate {
  id: string;
  appNumber: string;
  mark: string;
  date: string;
  type: 'status_change' | 'examination' | 'advertisement' | 'objection' | 'registration' | 'renewal_notice';
  title: string;
  detail: string;
  isNew?: boolean;
}

interface Objection {
  id: string;
  appNumber: string;
  mark: string;
  receivedDate: string;
  replyDeadline: string;
  daysLeft: number;
  grounds: string[];
  status: 'pending_reply' | 'reply_filed' | 'hearing_scheduled' | 'resolved';
  hearingDate?: string;
  severity: 'high' | 'medium' | 'low';
}

const trademarkApplications: TrademarkApplication[] = [
  {
    id: '1',
    mark: 'ELYSIUM',
    class: 'Class 3',
    appNumber: '5847291',
    filedDate: '2024-03-15',
    status: 'examination',
    renewalDue: undefined,
  },
  {
    id: '2',
    mark: 'ELYSIUM NOIR',
    class: 'Class 3',
    appNumber: '5847292',
    filedDate: '2024-01-10',
    status: 'objected',
    renewalDue: undefined,
  },
  {
    id: '3',
    mark: 'ELYSIUM GOLD',
    class: 'Class 35',
    appNumber: '4912847',
    filedDate: '2019-06-22',
    status: 'registered',
    renewalDue: '2029-06-22',
    daysToRenewal: 1400,
  },
  {
    id: '4',
    mark: 'ELYSIUM BEAUTY',
    class: 'Class 44',
    appNumber: '5102938',
    filedDate: '2020-11-05',
    status: 'registered',
    renewalDue: '2025-11-05',
    daysToRenewal: 75,
  },
];

const governmentUpdates: GovernmentUpdate[] = [
  {
    id: 'u1',
    appNumber: '5847291',
    mark: 'ELYSIUM',
    date: '2024-08-20',
    type: 'examination',
    title: 'Examination Report Issued',
    detail: 'Examiner has raised objections under Section 9(1)(b) — descriptive mark. Reply required within 30 days.',
    isNew: true,
  },
  {
    id: 'u2',
    appNumber: '5847292',
    mark: 'ELYSIUM NOIR',
    date: '2024-08-18',
    type: 'objection',
    title: 'Opposition Filed by Third Party',
    detail: 'Opposition filed by Noir Perfumes Ltd citing prior use under Section 11. Hearing scheduled for Oct 2024.',
    isNew: true,
  },
  {
    id: 'u3',
    appNumber: '5102938',
    mark: 'ELYSIUM BEAUTY',
    date: '2024-08-15',
    type: 'renewal_notice',
    title: 'Renewal Deadline Approaching',
    detail: 'Registration expires on 05-Nov-2025. Renewal application must be filed 6 months before expiry.',
    isNew: true,
  },
  {
    id: 'u4',
    appNumber: '4912847',
    mark: 'ELYSIUM GOLD',
    date: '2024-07-30',
    type: 'status_change',
    title: 'Status Updated: Registered',
    detail: 'Trademark successfully registered. Certificate of Registration issued. Valid until 22-Jun-2029.',
    isNew: false,
  },
  {
    id: 'u5',
    appNumber: '5847291',
    mark: 'ELYSIUM',
    date: '2024-07-10',
    type: 'advertisement',
    title: 'Advertised in Trade Marks Journal',
    detail: 'Mark published in Trade Marks Journal No. 2024/31. Opposition window open for 4 months.',
    isNew: false,
  },
  {
    id: 'u6',
    appNumber: '5847292',
    mark: 'ELYSIUM NOIR',
    date: '2024-06-25',
    type: 'examination',
    title: 'Application Accepted for Examination',
    detail: 'Application moved to examination queue. Estimated examination date: Q3 2024.',
    isNew: false,
  },
];

const objections: Objection[] = [
  {
    id: 'o1',
    appNumber: '5847291',
    mark: 'ELYSIUM',
    receivedDate: '2024-08-20',
    replyDeadline: '2024-09-19',
    daysLeft: 28,
    grounds: ['Section 9(1)(b) — Descriptive mark', 'Lacks distinctiveness for Class 3 goods'],
    status: 'pending_reply',
    severity: 'medium',
  },
  {
    id: 'o2',
    appNumber: '5847292',
    mark: 'ELYSIUM NOIR',
    receivedDate: '2024-08-18',
    replyDeadline: '2024-09-17',
    daysLeft: 26,
    grounds: ['Section 11 — Likelihood of confusion with prior mark', 'Prior use by Noir Perfumes Ltd since 2018'],
    status: 'hearing_scheduled',
    hearingDate: '2024-10-14',
    severity: 'high',
  },
];

const statusConfig: Record<string, { label: string; color: string; bg: string; dot: string }> = {
  pending: { label: 'Pending', color: 'text-ink-muted', bg: 'bg-surface-3', dot: 'bg-ink-subtle' },
  examination: { label: 'Under Examination', color: 'text-warning', bg: 'bg-warning/10', dot: 'bg-warning' },
  advertised: { label: 'Advertised', color: 'text-ai', bg: 'bg-ai-light', dot: 'bg-ai' },
  objected: { label: 'Objected', color: 'text-danger', bg: 'bg-danger/10', dot: 'bg-danger' },
  registered: { label: 'Registered', color: 'text-success', bg: 'bg-success-light', dot: 'bg-success' },
};

const updateTypeConfig: Record<string, { icon: string; color: string }> = {
  status_change: { icon: '◈', color: 'text-ai' },
  examination: { icon: '⊕', color: 'text-warning' },
  advertisement: { icon: '◻', color: 'text-ai' },
  objection: { icon: '⚠', color: 'text-danger' },
  registration: { icon: '✓', color: 'text-success' },
  renewal_notice: { icon: '⟳', color: 'text-warning' },
};

function formatDate(dateStr: string) {
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

function RenewalBadge({ days }: { days: number }) {
  if (days <= 90) return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-danger/10 text-danger">
      <span className="w-1.5 h-1.5 rounded-full bg-danger animate-pulse inline-block"></span>
      {days}d to renewal
    </span>
  );
  if (days <= 365) return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-warning/10 text-warning">
      <span className="w-1.5 h-1.5 rounded-full bg-warning inline-block"></span>
      {Math.round(days / 30)}mo to renewal
    </span>
  );
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-success-light text-success">
      <span className="w-1.5 h-1.5 rounded-full bg-success inline-block"></span>
      {Math.round(days / 365)}yr to renewal
    </span>
  );
}

export default function TrademarkStatusPage() {
  const [activeTab, setActiveTab] = useState<'overview' | 'feed' | 'objections'>('overview');
  const [selectedApp, setSelectedApp] = useState<string | null>(null);
  const [feedFilter, setFeedFilter] = useState<string>('all');

  const newUpdatesCount = governmentUpdates.filter(u => u.isNew).length;
  const urgentObjections = objections.filter(o => o.daysLeft <= 30 && o.status === 'pending_reply').length;
  const renewalAlerts = trademarkApplications.filter(a => a.daysToRenewal !== undefined && a.daysToRenewal <= 365).length;

  const filteredUpdates = feedFilter === 'all'
    ? governmentUpdates
    : governmentUpdates.filter(u => u.type === feedFilter);

  const filteredUpdatesForApp = selectedApp
    ? filteredUpdates.filter(u => u.appNumber === selectedApp)
    : filteredUpdates;

  return (
    <div className="min-h-screen bg-surface-2 font-sans">
      <AppNav variant="app" activePath="/trademark/status" />

      <main className="sm:ml-60 pt-14 sm:pt-0 min-h-screen">
        <div className="max-w-6xl mx-auto px-6 py-8">

          {/* Header */}
          <div className="flex items-center gap-3 mb-2">
            <Link href="/trademark" className="text-ink-muted hover:text-ink text-[13px] transition-colors">← Trademark Search</Link>
          </div>
          <div className="flex items-start justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-ink mb-1">Trademark Status Monitor</h1>
              <p className="text-[14px] text-ink-muted">Track applications, government updates, and manage objection deadlines</p>
            </div>
            <button className="hidden sm:flex items-center gap-2 px-4 py-2 bg-ai text-white text-[13px] font-semibold rounded-lg hover:bg-ai-dark transition-colors">
              <span>⟳</span> Sync with IP India
            </button>
          </div>

          {/* Alert Banner */}
          {(urgentObjections > 0 || renewalAlerts > 0) && (
            <div className="mb-6 p-4 bg-danger/5 border border-danger/20 rounded-xl flex items-start gap-3">
              <span className="text-danger text-lg mt-0.5">⚠</span>
              <div className="flex-1">
                <p className="text-[13px] font-semibold text-danger mb-1">Action Required</p>
                <div className="flex flex-wrap gap-3">
                  {urgentObjections > 0 && (
                    <button onClick={() => setActiveTab('objections')} className="text-[12px] text-danger underline underline-offset-2">
                      {urgentObjections} objection{urgentObjections > 1 ? 's' : ''} need reply within 30 days
                    </button>
                  )}
                  {renewalAlerts > 0 && (
                    <span className="text-[12px] text-danger">
                      {renewalAlerts} trademark{renewalAlerts > 1 ? 's' : ''} due for renewal within 12 months
                    </span>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Stats Row */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {[
              { label: 'Total Applications', value: trademarkApplications.length, icon: '™', color: 'text-ai', bg: 'bg-ai-light' },
              { label: 'Registered', value: trademarkApplications.filter(a => a.status === 'registered').length, icon: '✓', color: 'text-success', bg: 'bg-success-light' },
              { label: 'New Gov. Updates', value: newUpdatesCount, icon: '◈', color: 'text-warning', bg: 'bg-warning/10' },
              { label: 'Open Objections', value: objections.filter(o => o.status !== 'resolved').length, icon: '⚠', color: 'text-danger', bg: 'bg-danger/10' },
            ].map(stat => (
              <div key={stat.label} className="bg-white rounded-xl border border-border p-4 shadow-card">
                <div className="flex items-center gap-2 mb-2">
                  <div className={`w-7 h-7 rounded-lg ${stat.bg} flex items-center justify-center`}>
                    <span className={`text-sm ${stat.color}`}>{stat.icon}</span>
                  </div>
                </div>
                <p className="text-2xl font-bold text-ink">{stat.value}</p>
                <p className="text-[12px] text-ink-muted mt-0.5">{stat.label}</p>
              </div>
            ))}
          </div>

          {/* Tabs */}
          <div className="flex gap-1 mb-6 bg-surface-3 p-1 rounded-xl w-fit">
            {[
              { id: 'overview', label: 'Applications', icon: '◻' },
              { id: 'feed', label: `Gov. Updates${newUpdatesCount > 0 ? ` (${newUpdatesCount})` : ''}`, icon: '◈' },
              { id: 'objections', label: `Objections${objections.filter(o => o.status !== 'resolved').length > 0 ? ` (${objections.filter(o => o.status !== 'resolved').length})` : ''}`, icon: '⚠' },
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as typeof activeTab)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-[13px] font-medium transition-all ${
                  activeTab === tab.id ? 'bg-white text-ink shadow-card' : 'text-ink-muted hover:text-ink'
                }`}
              >
                <span>{tab.icon}</span>
                {tab.label}
              </button>
            ))}
          </div>

          {/* ── OVERVIEW TAB ── */}
          {activeTab === 'overview' && (
            <div className="space-y-3">
              {trademarkApplications.map(app => {
                const sc = statusConfig[app.status];
                return (
                  <div key={app.id} className="bg-white rounded-xl border border-border shadow-card hover:shadow-card-hover transition-shadow">
                    <div className="p-5">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex items-start gap-4">
                          <div className="w-10 h-10 rounded-xl bg-ai-light flex items-center justify-center flex-shrink-0">
                            <span className="text-ai font-bold text-[13px]">™</span>
                          </div>
                          <div>
                            <div className="flex items-center gap-2 flex-wrap">
                              <h3 className="font-bold text-ink text-[15px]">{app.mark}</h3>
                              <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-semibold ${sc.bg} ${sc.color}`}>
                                <span className={`w-1.5 h-1.5 rounded-full ${sc.dot}`}></span>
                                {sc.label}
                              </span>
                            </div>
                            <div className="flex items-center gap-3 mt-1 flex-wrap">
                              <span className="text-[12px] text-ink-muted">{app.class}</span>
                              <span className="text-ink-subtle text-[12px]">·</span>
                              <span className="text-[12px] text-ink-muted font-mono">App #{app.appNumber}</span>
                              <span className="text-ink-subtle text-[12px]">·</span>
                              <span className="text-[12px] text-ink-muted">Filed {formatDate(app.filedDate)}</span>
                            </div>
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-2 flex-shrink-0">
                          {app.daysToRenewal !== undefined && (
                            <RenewalBadge days={app.daysToRenewal} />
                          )}
                          {app.renewalDue && (
                            <span className="text-[11px] text-ink-muted">Renew by {formatDate(app.renewalDue)}</span>
                          )}
                        </div>
                      </div>

                      {/* Status Progress Bar */}
                      <div className="mt-4">
                        <div className="flex items-center justify-between mb-1.5">
                          {['Filed', 'Examination', 'Advertised', 'Registered'].map((stage, i) => {
                            const stageOrder = ['pending', 'examination', 'advertised', 'registered'];
                            const currentIdx = stageOrder.indexOf(app.status === 'objected' ? 'examination' : app.status);
                            const isActive = i <= currentIdx;
                            const isCurrent = i === currentIdx;
                            return (
                              <div key={stage} className="flex flex-col items-center gap-1 flex-1">
                                <div className={`w-2.5 h-2.5 rounded-full border-2 transition-all ${
                                  isCurrent && app.status === 'objected' ? 'border-danger bg-danger' : isActive ?'border-ai bg-ai' : 'border-border bg-white'
                                }`}></div>
                                <span className={`text-[10px] font-medium ${isActive ? 'text-ink' : 'text-ink-subtle'}`}>{stage}</span>
                              </div>
                            );
                          })}
                        </div>
                        <div className="relative h-1 bg-surface-3 rounded-full">
                          {(() => {
                            const stageOrder = ['pending', 'examination', 'advertised', 'registered'];
                            const idx = stageOrder.indexOf(app.status === 'objected' ? 'examination' : app.status);
                            const pct = Math.round(((idx + 1) / 4) * 100);
                            return (
                              <div
                                className={`absolute left-0 top-0 h-full rounded-full transition-all ${app.status === 'objected' ? 'bg-danger' : 'bg-ai'}`}
                                style={{ width: `${pct}%` }}
                              ></div>
                            );
                          })()}
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-2 mt-4 pt-4 border-t border-border">
                        <button
                          onClick={() => { setSelectedApp(app.appNumber === selectedApp ? null : app.appNumber); setActiveTab('feed'); }}
                          className="text-[12px] font-medium text-ai hover:text-ai-dark transition-colors"
                        >
                          View Updates →
                        </button>
                        {app.status === 'objected' && (
                          <button
                            onClick={() => setActiveTab('objections')}
                            className="text-[12px] font-medium text-danger hover:text-danger/80 transition-colors ml-3"
                          >
                            Manage Objection →
                          </button>
                        )}
                        {app.daysToRenewal !== undefined && app.daysToRenewal <= 365 && (
                          <button className="ml-auto text-[12px] font-semibold text-white bg-warning px-3 py-1.5 rounded-lg hover:bg-warning/90 transition-colors">
                            File Renewal
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* ── GOVERNMENT FEED TAB ── */}
          {activeTab === 'feed' && (
            <div>
              {/* Filter Bar */}
              <div className="flex items-center gap-2 mb-5 flex-wrap">
                {[
                  { id: 'all', label: 'All Updates' },
                  { id: 'objection', label: 'Objections' },
                  { id: 'examination', label: 'Examination' },
                  { id: 'advertisement', label: 'Advertisement' },
                  { id: 'renewal_notice', label: 'Renewal' },
                  { id: 'registration', label: 'Registration' },
                ].map(f => (
                  <button
                    key={f.id}
                    onClick={() => setFeedFilter(f.id)}
                    className={`px-3 py-1.5 rounded-lg text-[12px] font-medium transition-all ${
                      feedFilter === f.id ? 'bg-ai text-white' : 'bg-white border border-border text-ink-muted hover:text-ink'
                    }`}
                  >
                    {f.label}
                  </button>
                ))}
                {selectedApp && (
                  <button
                    onClick={() => setSelectedApp(null)}
                    className="ml-auto flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] font-medium bg-ai-light text-ai border border-ai/20"
                  >
                    App #{selectedApp} <span className="text-ai/60">×</span>
                  </button>
                )}
              </div>

              {/* Feed */}
              <div className="relative">
                <div className="absolute left-5 top-0 bottom-0 w-px bg-border"></div>
                <div className="space-y-0">
                  {filteredUpdatesForApp.map((update, idx) => {
                    const tc = updateTypeConfig[update.type];
                    return (
                      <div key={update.id} className="relative pl-14 pb-6">
                        {/* Timeline dot */}
                        <div className={`absolute left-3 top-1 w-5 h-5 rounded-full border-2 border-white flex items-center justify-center ${
                          update.isNew ? 'bg-ai' : 'bg-surface-3'
                        }`}>
                          <span className={`text-[9px] ${update.isNew ? 'text-white' : tc.color}`}>{tc.icon}</span>
                        </div>

                        <div className={`bg-white rounded-xl border shadow-card p-4 ${update.isNew ? 'border-ai/30 ring-1 ring-ai/10' : 'border-border'}`}>
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 flex-wrap mb-1">
                                {update.isNew && (
                                  <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-ai text-white uppercase tracking-wide">New</span>
                                )}
                                <span className="font-semibold text-ink text-[14px]">{update.title}</span>
                              </div>
                              <div className="flex items-center gap-2 mb-2">
                                <span className="text-[11px] font-mono text-ink-muted bg-surface-3 px-2 py-0.5 rounded">#{update.appNumber}</span>
                                <span className="text-[11px] font-semibold text-ink-muted">{update.mark}</span>
                              </div>
                              <p className="text-[13px] text-ink-muted leading-relaxed">{update.detail}</p>
                            </div>
                            <span className="text-[11px] text-ink-subtle flex-shrink-0 mt-0.5">{formatDate(update.date)}</span>
                          </div>

                          {update.type === 'objection' && (
                            <div className="mt-3 pt-3 border-t border-border flex items-center gap-2">
                              <button
                                onClick={() => setActiveTab('objections')}
                                className="text-[12px] font-semibold text-danger hover:text-danger/80 transition-colors"
                              >
                                View Objection Details →
                              </button>
                            </div>
                          )}
                          {update.type === 'renewal_notice' && (
                            <div className="mt-3 pt-3 border-t border-border flex items-center gap-2">
                              <button className="text-[12px] font-semibold text-warning hover:text-warning/80 transition-colors">
                                Schedule Renewal Reminder →
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                  {filteredUpdatesForApp.length === 0 && (
                    <div className="pl-14 py-8 text-center text-ink-muted text-[13px]">No updates match the selected filter.</div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* ── OBJECTIONS TAB ── */}
          {activeTab === 'objections' && (
            <div className="space-y-4">
              {objections.map(obj => {
                const isUrgent = obj.daysLeft <= 14;
                const isWarning = obj.daysLeft <= 30 && !isUrgent;
                const severityColor = obj.severity === 'high' ? 'text-danger' : obj.severity === 'medium' ? 'text-warning' : 'text-ai';
                const severityBg = obj.severity === 'high' ? 'bg-danger/10' : obj.severity === 'medium' ? 'bg-warning/10' : 'bg-ai-light';

                return (
                  <div key={obj.id} className={`bg-white rounded-xl border shadow-card overflow-hidden ${
                    isUrgent ? 'border-danger/40' : isWarning ? 'border-warning/40' : 'border-border'
                  }`}>
                    {/* Urgency Strip */}
                    {(isUrgent || isWarning) && (
                      <div className={`px-5 py-2 text-[11px] font-semibold flex items-center gap-2 ${
                        isUrgent ? 'bg-danger/10 text-danger' : 'bg-warning/10 text-warning'
                      }`}>
                        <span className={`w-1.5 h-1.5 rounded-full animate-pulse ${isUrgent ? 'bg-danger' : 'bg-warning'}`}></span>
                        {isUrgent ? `URGENT: Only ${obj.daysLeft} days left to file reply` : `Reply deadline in ${obj.daysLeft} days`}
                      </div>
                    )}

                    <div className="p-5">
                      <div className="flex items-start justify-between gap-4 mb-4">
                        <div>
                          <div className="flex items-center gap-2 flex-wrap mb-1">
                            <h3 className="font-bold text-ink text-[15px]">{obj.mark}</h3>
                            <span className={`px-2.5 py-0.5 rounded-full text-[11px] font-semibold ${severityBg} ${severityColor}`}>
                              {obj.severity.charAt(0).toUpperCase() + obj.severity.slice(1)} Risk
                            </span>
                          </div>
                          <span className="text-[12px] font-mono text-ink-muted bg-surface-3 px-2 py-0.5 rounded">App #{obj.appNumber}</span>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p className="text-[11px] text-ink-muted">Reply Deadline</p>
                          <p className={`text-[14px] font-bold ${isUrgent ? 'text-danger' : isWarning ? 'text-warning' : 'text-ink'}`}>
                            {formatDate(obj.replyDeadline)}
                          </p>
                        </div>
                      </div>

                      {/* Grounds */}
                      <div className="mb-4">
                        <p className="text-[11px] font-semibold text-ink-muted uppercase tracking-wide mb-2">Grounds of Objection</p>
                        <div className="space-y-1.5">
                          {obj.grounds.map((g, i) => (
                            <div key={i} className="flex items-start gap-2">
                              <span className="text-danger text-[12px] mt-0.5">◆</span>
                              <span className="text-[13px] text-ink">{g}</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Timeline */}
                      <div className="bg-surface-2 rounded-xl p-4 mb-4">
                        <p className="text-[11px] font-semibold text-ink-muted uppercase tracking-wide mb-3">Objection Timeline</p>
                        <div className="flex items-center gap-0">
                          {[
                            { label: 'Received', date: obj.receivedDate, done: true },
                            { label: 'Reply Filed', date: obj.status !== 'pending_reply' ? obj.replyDeadline : undefined, done: obj.status !== 'pending_reply' },
                            { label: 'Hearing', date: obj.hearingDate, done: obj.status === 'resolved', active: obj.status === 'hearing_scheduled' },
                            { label: 'Resolved', date: undefined, done: obj.status === 'resolved' },
                          ].map((step, i, arr) => (
                            <React.Fragment key={step.label}>
                              <div className="flex flex-col items-center gap-1 flex-1">
                                <div className={`w-3 h-3 rounded-full border-2 ${
                                  step.done ? 'bg-success border-success' : step.active ?'bg-warning border-warning': 'bg-white border-border'
                                }`}></div>
                                <span className={`text-[10px] font-medium text-center ${step.done || step.active ? 'text-ink' : 'text-ink-subtle'}`}>{step.label}</span>
                                {step.date && <span className="text-[9px] text-ink-subtle">{formatDate(step.date)}</span>}
                              </div>
                              {i < arr.length - 1 && (
                                <div className={`flex-1 h-0.5 mb-4 ${step.done ? 'bg-success' : 'bg-border'}`}></div>
                              )}
                            </React.Fragment>
                          ))}
                        </div>
                      </div>

                      {/* Status Badge + Actions */}
                      <div className="flex items-center justify-between gap-3 pt-4 border-t border-border">
                        <div className="flex items-center gap-2">
                          <span className={`px-2.5 py-1 rounded-lg text-[12px] font-semibold ${
                            obj.status === 'pending_reply' ? 'bg-danger/10 text-danger' :
                            obj.status === 'reply_filed' ? 'bg-ai-light text-ai' :
                            obj.status === 'hearing_scheduled'? 'bg-warning/10 text-warning' : 'bg-success-light text-success'
                          }`}>
                            {obj.status === 'pending_reply' ? 'Reply Pending' :
                             obj.status === 'reply_filed' ? 'Reply Filed' :
                             obj.status === 'hearing_scheduled' ? `Hearing: ${obj.hearingDate ? formatDate(obj.hearingDate) : '—'}` :
                             'Resolved'}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Link
                            href="/examination"
                            className="text-[12px] font-medium text-ai hover:text-ai-dark transition-colors"
                          >
                            Analyze Report →
                          </Link>
                          {obj.status === 'pending_reply' && (
                            <button className="px-4 py-2 bg-ai text-white text-[12px] font-semibold rounded-lg hover:bg-ai-dark transition-colors">
                              Draft Reply
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}

              {/* Renewal Deadline Alerts */}
              <div className="mt-6">
                <h2 className="text-[14px] font-bold text-ink mb-3 flex items-center gap-2">
                  <span className="text-warning">⟳</span> Renewal Deadline Alerts
                </h2>
                <div className="space-y-3">
                  {trademarkApplications
                    .filter(a => a.daysToRenewal !== undefined)
                    .sort((a, b) => (a.daysToRenewal ?? 9999) - (b.daysToRenewal ?? 9999))
                    .map(app => (
                      <div key={app.id} className="bg-white rounded-xl border border-border shadow-card p-4 flex items-center justify-between gap-4">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-xl bg-ai-light flex items-center justify-center">
                            <span className="text-ai font-bold text-[12px]">™</span>
                          </div>
                          <div>
                            <p className="font-semibold text-ink text-[14px]">{app.mark}</p>
                            <p className="text-[12px] text-ink-muted">{app.class} · #{app.appNumber}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          {app.renewalDue && <span className="text-[12px] text-ink-muted hidden sm:block">Due {formatDate(app.renewalDue)}</span>}
                          {app.daysToRenewal !== undefined && <RenewalBadge days={app.daysToRenewal} />}
                          {app.daysToRenewal !== undefined && app.daysToRenewal <= 365 && (
                            <button className="px-3 py-1.5 bg-warning text-white text-[12px] font-semibold rounded-lg hover:bg-warning/90 transition-colors">
                              Renew Now
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            </div>
          )}

        </div>
      </main>
    </div>
  );
}
